// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map, switchMap, take, filter } from 'rxjs/operators';
import {
  RoleDetail,
  Role,
  RolesService,
  readParameterFromRouterState,
  IAM_ROUTE,
  RoleGroupSelections,
  AlertType,
  GoAction,
  QueueAlertAction,
  Loadable,
  Loaded,
  ScopeCategory,
  MaybeLoadable,
  needsLoading,
  ScopeConstraint
} from '@app/core';
import {
  State,
  selectRoleDetail,
  LoadingRoleDetailsAction,
  selectScopeCategories,
  LoadingScopeCategoriesAction,
  SelectScopeCategoryAction,
  selectSelectedRoleScopeCategory,
  ClearRoleGroupSelectionsAction,
  selectRoleGroupSelections,
  SelectScopeConstraintsAction,
  SelectScopeCategoryForGroupAction,
  SelectScopeConstraintsForGroupAction,
  selectSelectedRoleScopeCategoryForGroup
} from '@app/access-management/state';
import { DataTableAjaxComponent, SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';

const shouldLoadRole = (roleDetail: RoleDetail | undefined) => !roleDetail || !roleDetail.role;

const shouldLoadCategories = (categories: MaybeLoadable) => needsLoading<ScopeCategory[]>(categories);

@Component({
  selector: 'app-role-details-group-add-scope',
  templateUrl: './role-details-group-add-scope.component.html',
  styleUrls: ['./role-details-group-add-scope.component.scss']
})
export class RoleDetailsGroupAddScopeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('actionTemplate') actionTemplate: any;
  @ViewChild(DataTableAjaxComponent) dataTableAjax: DataTableAjaxComponent | undefined;

  columns: any[] = [];

  readonly role$: Observable<Role | undefined>;
  readonly isSubmitting$ = new BehaviorSubject<boolean>(false);
  readonly failedSubmit$ = new BehaviorSubject<boolean>(false);

  scopeCategories$: Observable<Loadable | Loaded<ScopeCategory> | undefined>;
  selectedScopeCategory$!: Observable<ScopeCategory | undefined>;
  roleId: string;
  selectedCategory: any;

  private submitSubscription: Subscription | undefined = undefined;

  constructor(private activatedRoute: ActivatedRoute, private store: Store<State>, private rolesService: RolesService) {
    this.roleId = this.getRoleId();

    const maybeRoleDetail$ = this.store.pipe(select(selectRoleDetail(this.roleId)));
    this.loadRoleDetailsIfNecessary(maybeRoleDetail$);
    this.role$ = this.getRole$(maybeRoleDetail$);

    this.scopeCategories$ = this.store.pipe(select(selectScopeCategories));
    // load if necesary
    this.scopeCategories$
      .pipe(
        take(1),
        filter(shouldLoadCategories)
      )
      .subscribe(() => this.store.dispatch(new LoadingScopeCategoriesAction()));
  }

  selectCategory(category: ScopeCategory) {
    this.selectedCategory = category;
    this.store.dispatch(new SelectScopeCategoryForGroupAction({ roleId: this.roleId, category }));
  }

  ngOnInit() {
    this.selectedScopeCategory$ =
      this.store.pipe(select(selectSelectedRoleScopeCategoryForGroup(this.roleId)));
  }

  ngOnDestroy() {
    if (this.submitSubscription) {
      this.submitSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.columns = [
      {
        name: 'Name',
        prop: 'name',
        flexGrow: 2
      }
    ];
  }

  onSelectConstraints(rows: SelectedRows) {
    const constraints = rows.selected;
    this.selectConstraints(constraints);
  }

  selectConstraints(constraints: ScopeConstraint[]) {
    this.store.dispatch(
      new SelectScopeConstraintsForGroupAction({
        roleId: this.roleId,
        constraints
      })
    );
  }

  submit() {
    this.setSubmitting();
    this.submitSubscription = this.store
      .pipe(
        select(selectRoleGroupSelections(this.roleId)),
        switchMap((roleGroupSelections: RoleGroupSelections) => {
          return this.rolesService.addGroupsToRole(this.roleId, roleGroupSelections, this.selectedCategory);
        })
      )
      .subscribe(
        () => {
          this.goToRoleDetailsPage();
          this.store.dispatch(new LoadingRoleDetailsAction(this.roleId));
          this.store.dispatch(
            new QueueAlertAction({
              alertText: `Groups have successfully been assigned to this role.`,
              alertType: AlertType.SUCCESS
            })
          );
        },
        () => {
          this.failSubmitting();
          this.store.dispatch(
            new QueueAlertAction({
              alertText: `Failed to add group to Role`,
              alertType: AlertType.ERROR
            })
          );
        }
      );
  }

  cancel() {
    this.store.dispatch(new ClearRoleGroupSelectionsAction(this.roleId));
    this.goToRoleDetailsPage();
  }

  private goToRoleDetailsPage() {
    this.store.dispatch(new GoAction(['/', IAM_ROUTE, 'roles', this.roleId]));
  }

  private setSubmitting() {
    this.isSubmitting$.next(true);
    this.failedSubmit$.next(false);
  }

  private failSubmitting() {
    this.isSubmitting$.next(false);
    this.failedSubmit$.next(true);
  }

  private getRoleId() {
    const roleId = readParameterFromRouterState(this.activatedRoute, 'id');
    if (!roleId) {
      throw new Error('role id not in route');
    }
    return roleId;
  }

  private getRole$(maybeRoleDetail$: Observable<RoleDetail | undefined>) {
    const roleDetail$: Observable<RoleDetail> = maybeRoleDetail$.pipe(
      filter<RoleDetail>(maybeRoleDetail => maybeRoleDetail !== undefined)
    );
    return roleDetail$.pipe(map(roleDetail => roleDetail.role));
  }

  private loadRoleDetailsIfNecessary(maybeRoleDetail$: Observable<RoleDetail | undefined>) {
    return maybeRoleDetail$
      .pipe(take(1), filter(shouldLoadRole))
      .subscribe(() => this.store.dispatch(new LoadingRoleDetailsAction(this.roleId)));
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-details/role-details-group-add-scope/role-details-group-add-scope.component.ts